import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useDisclosure,
  HStack,
  VStack,
  Text,
  Badge,
  IconButton,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  CloseButton,
  Avatar,
  Tooltip,
} from '@chakra-ui/react';
import { FaEdit, FaTrash, FaPlus, FaUserShield, FaEye, FaKey } from 'react-icons/fa';
import PageTable from 'components/layout/PageTable';
import rolePermissionService from 'services/rolePermissionService';
import authService from 'services/authService';
import UserFormModal from './UserFormModal';
import UserRoleModal from './UserRoleModal';
import UserPermissionsModal from './UserPermissionsModal';
import ResetPasswordModal from './ResetPasswordModal';

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const { isOpen: isFormOpen, onOpen: onFormOpen, onClose: onFormClose } = useDisclosure();
  const { isOpen: isRoleOpen, onOpen: onRoleOpen, onClose: onRoleClose } = useDisclosure();
  const { isOpen: isPermissionsOpen, onOpen: onPermissionsOpen, onClose: onPermissionsClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const {
    isOpen: isResetOpen,
    onOpen: onResetOpen,
    onClose: onResetClose,
  } = useDisclosure();

  const toast = useToast();
  const cancelRef = React.useRef();

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersResponse, rolesResponse] = await Promise.all([
        rolePermissionService.getUsers(),
        rolePermissionService.getRoles()
      ]);
      
      setUsers(usersResponse.payload?.users || []);
      setRoles(rolesResponse.payload?.roles || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: 'Error',
        description: 'Error al cargar los datos',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = () => {
    setSelectedUser(null);
    setIsEditing(false);
    onFormOpen();
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setIsEditing(true);
    onFormOpen();
  };

  const handleAssignRole = (user) => {
    setSelectedUser(user);
    onRoleOpen();
  };

  const handleViewPermissions = (user) => {
    setSelectedUser(user);
    onPermissionsOpen();
  };

  const handleResetPassword = (user) => {
    setSelectedUser(user);
    onResetOpen();
  };

  const handleDeleteUser = (user) => {
    setUserToDelete(user);
    onDeleteOpen();
  };

  const confirmDelete = async () => {
    try {
      // En lugar de eliminar, desactivamos el usuario
      await rolePermissionService.updateUser(userToDelete.id, { 
        enabled: false 
      });
      
      toast({
        title: 'Éxito',
        description: 'Usuario desactivado correctamente',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      loadData();
    } catch (error) {
      console.error('Error deactivating user:', error);
      toast({
        title: 'Error',
        description: 'Error al desactivar el usuario',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      onDeleteClose();
      setUserToDelete(null);
    }
  };

  const handleFormSuccess = () => {
    loadData();
    onFormClose();
    toast({
      title: 'Éxito',
      description: `Usuario ${isEditing ? 'actualizado' : 'creado'} correctamente`,
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  const handleResetSuccess = () => {
    onResetClose();
    toast({
      title: 'Contraseña reseteada',
      description: 'Deberá cambiarla al ingresar.',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  const handleRoleSuccess = () => {
    loadData();
    onRoleClose();
    toast({
      title: 'Éxito',
      description: 'Rol asignado correctamente',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  const getRoleBadgeColor = (roleName) => {
    switch (roleName) {
      case 'Admin': return 'purple';
      case 'Mecanico': return 'blue';
      case 'Recepcionista': return 'green';
      default: return 'gray';
    }
  };

  // Verificar permisos
  const canCreateUsers = authService.hasPermission('users.create');
  const canEditUsers = authService.hasPermission('users.edit');
  const canDeleteUsers = authService.hasPermission('users.delete');
  const canViewUsers = authService.hasPermission('users.view');
  const canAssignRoles = authService.hasPermission('permissions.assign');

  if (!canViewUsers) {
    return (
      <Box p={4}>
        <Text color="red.500">No tienes permisos para ver los usuarios</Text>
      </Box>
    );
  }

  return (
    <PageTable maxW="100%">
      <VStack spacing={4} align="stretch">
        <HStack justify="space-between" flexWrap="wrap" gap={4}>
          {canCreateUsers && (
            <Button
              leftIcon={<FaPlus />}
              colorScheme="gray"
              size={{ base: "sm", md: "md" }}
              onClick={handleCreateUser}
              ml="auto"
            >
              Nuevo Usuario
            </Button>
          )}
        </HStack>

        <Box overflowX="hidden" w="100%" maxW="100%" minW={0}>
          {loading ? (
            <Text>Cargando usuarios...</Text>
          ) : (
            <Box>
              {/* Vista de escritorio */}
              <Box display={{ base: "none", lg: "block" }}>
                <PageTable.Scroll>
                  <Table variant="simple" size="sm">
                                    <Thead>
                                      <Tr>
                                        <Th>Usuario</Th>
                                        <Th>Usuario / Email</Th>
                                        <Th>Rol</Th>
                                        <Th>Estado</Th>
                                        <Th>Fecha Creación</Th>
                                        <Th>Acciones</Th>
                                      </Tr>
                                    </Thead>
                                    <Tbody>
                                      {users.map((user) => (
                                        <Tr key={user.id}>
                                          <Td>
                                            <HStack spacing={3}>
                                              <Avatar size="sm" name={user.name} />
                                              <VStack align="start" spacing={0}>
                                                <Text fontWeight="normal" fontSize="sm" noOfLines={1}>
                                                  {user.name}
                                                </Text>
                                                <Text fontSize="xs" color="gray.500">
                                                  ID: {user.id}
                                                </Text>
                                              </VStack>
                                            </HStack>
                                          </Td>
                                          <Td>
                                            <Text fontSize="sm" noOfLines={1} title={user.user + (user.email ? ` (${user.email})` : '')}>
                                              {user.user}
                                              {user.email && (
                                                <Text fontSize="xs" color="gray.500">{user.email}</Text>
                                              )}
                                            </Text>
                                          </Td>
                                          <Td>
                                            {user.role ? (
                                              <Badge colorScheme={getRoleBadgeColor(user.role.name)} size="sm">
                                                {user.role.name}
                                              </Badge>
                                            ) : (
                                              <Badge colorScheme="red" size="sm">Sin Rol</Badge>
                                            )}
                                          </Td>
                                          <Td>
                                            <Badge 
                                              colorScheme={user.enabled !== false ? 'green' : 'red'}
                                              size="sm"
                                            >
                                              {user.enabled !== false ? 'Activo' : 'Inactivo'}
                                            </Badge>
                                          </Td>
                                          <Td>
                                            <Text fontSize="sm">
                                              {new Date(user.createdAt).toLocaleDateString()}
                                            </Text>
                                          </Td>
                                          <Td>
                                            <HStack spacing={1} justify="start" wrap="wrap">
                                              <Tooltip label="Ver permisos">
                                                <IconButton
                                                  icon={<FaEye />}
                                                  size="sm"
                                                  colorScheme="gray"
                                                  variant="ghost"
                                                  onClick={() => handleViewPermissions(user)}
                                                  aria-label="Ver permisos"
                                                />
                                              </Tooltip>
                                              
                                              {canAssignRoles && (
                                                <Tooltip label="Asignar rol">
                                                  <IconButton
                                                    icon={<FaUserShield />}
                                                    size="sm"
                                                    colorScheme="gray"
                                                    variant="ghost"
                                                    onClick={() => handleAssignRole(user)}
                                                    aria-label="Asignar rol"
                                                  />
                                                </Tooltip>
                                              )}
                                              
                                              {canEditUsers && (
                                                <Tooltip label="Editar usuario">
                                                  <IconButton
                                                    icon={<FaEdit />}
                                                    size="sm"
                                                    colorScheme="gray"
                                                    variant="ghost"
                                                    onClick={() => handleEditUser(user)}
                                                    aria-label="Editar usuario"
                                                  />
                                                </Tooltip>
                                              )}

                                              {canEditUsers && (
                                                <Tooltip label="Resetear contraseña">
                                                  <IconButton
                                                    icon={<FaKey />}
                                                    size="sm"
                                                    colorScheme="gray"
                                                    variant="ghost"
                                                    onClick={() => handleResetPassword(user)}
                                                    aria-label="Resetear contraseña"
                                                  />
                                                </Tooltip>
                                              )}
                                              
                                              {canDeleteUsers && user.id !== authService.getCurrentUser()?.id && (
                                                <Tooltip label="Desactivar usuario">
                                                  <IconButton
                                                    icon={<FaTrash />}
                                                    size="sm"
                                                    colorScheme="red"
                                                    variant="ghost"
                                                    onClick={() => handleDeleteUser(user)}
                                                    aria-label="Desactivar usuario"
                                                  />
                                                </Tooltip>
                                              )}
                                            </HStack>
                                          </Td>
                                        </Tr>
                                      ))}
                                    </Tbody>
                                  </Table>
                </PageTable.Scroll>
              </Box>

              {/* Vista móvil/tablet */}
              <Box display={{ base: "block", lg: "none" }}>
                <VStack spacing={4}>
                  {users.map((user) => (
                    <Box 
                      key={user.id}
                      w="100%"
                      p={4}
                      borderRadius="md"
                      border="1px"
                      borderColor="gray.200"
                      bg="white"
                    >
                      <VStack spacing={3} align="start">
                        <HStack spacing={3} w="100%">
                          <Avatar size="md" name={user.name} />
                          <VStack align="start" spacing={1} flex={1}>
                            <Text fontWeight="bold" fontSize="md">
                              {user.name}
                            </Text>
                            <Text fontSize="sm" color="gray.600">
                              {user.user}
                              {user.email && (
                                <Text fontSize="xs" color="gray.500">{user.email}</Text>
                              )}
                            </Text>
                          </VStack>
                          <VStack spacing={1}>
                            {user.role ? (
                              <Badge colorScheme={getRoleBadgeColor(user.role.name)}>
                                {user.role.name}
                              </Badge>
                            ) : (
                              <Badge colorScheme="red">Sin Rol</Badge>
                            )}
                            <Badge 
                              colorScheme={user.enabled !== false ? 'green' : 'red'}
                            >
                              {user.enabled !== false ? 'Activo' : 'Inactivo'}
                            </Badge>
                          </VStack>
                        </HStack>
                        
                        <HStack justify="space-between" w="100%">
                          <Text fontSize="xs" color="gray.500">
                            Creado: {new Date(user.createdAt).toLocaleDateString()}
                          </Text>
                          <HStack spacing={1}>
                            <Tooltip label="Ver permisos">
                              <IconButton
                                icon={<FaEye />}
                                size="sm"
                                colorScheme="gray"
                                variant="ghost"
                                onClick={() => handleViewPermissions(user)}
                                aria-label="Ver permisos"
                              />
                            </Tooltip>
                            
                            {canAssignRoles && (
                              <Tooltip label="Asignar rol">
                                <IconButton
                                  icon={<FaUserShield />}
                                  size="sm"
                                  colorScheme="gray"
                                  variant="ghost"
                                  onClick={() => handleAssignRole(user)}
                                  aria-label="Asignar rol"
                                />
                              </Tooltip>
                            )}
                            
                            {canEditUsers && (
                              <Tooltip label="Editar usuario">
                                <IconButton
                                  icon={<FaEdit />}
                                  size="sm"
                                  colorScheme="gray"
                                  variant="ghost"
                                  onClick={() => handleEditUser(user)}
                                  aria-label="Editar usuario"
                                />
                              </Tooltip>
                            )}

                            {canEditUsers && (
                              <Tooltip label="Resetear contraseña">
                                <IconButton
                                  icon={<FaKey />}
                                  size="sm"
                                  colorScheme="gray"
                                  variant="ghost"
                                  onClick={() => handleResetPassword(user)}
                                  aria-label="Resetear contraseña"
                                />
                              </Tooltip>
                            )}
                            
                            {canDeleteUsers && user.id !== authService.getCurrentUser()?.id && (
                              <Tooltip label="Desactivar usuario">
                                <IconButton
                                  icon={<FaTrash />}
                                  size="sm"
                                  colorScheme="red"
                                  variant="ghost"
                                  onClick={() => handleDeleteUser(user)}
                                  aria-label="Desactivar usuario"
                                />
                              </Tooltip>
                            )}
                          </HStack>
                        </HStack>
                      </VStack>
                    </Box>
                  ))}
                </VStack>
              </Box>
            </Box>
          )}
        </Box>
      </VStack>

      {/* Modales */}
      <ResetPasswordModal
        isOpen={isResetOpen}
        onClose={onResetClose}
        user={selectedUser}
        onSuccess={handleResetSuccess}
      />

      <UserFormModal
        isOpen={isFormOpen}
        onClose={onFormClose}
        user={selectedUser}
        roles={roles}
        isEditing={isEditing}
        onSuccess={handleFormSuccess}
      />

      <UserRoleModal
        isOpen={isRoleOpen}
        onClose={onRoleClose}
        user={selectedUser}
        roles={roles}
        onSuccess={handleRoleSuccess}
      />

      <UserPermissionsModal
        isOpen={isPermissionsOpen}
        onClose={onPermissionsClose}
        user={selectedUser}
      />

      {/* Diálogo de confirmación */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
        closeOnOverlayClick={false}
      >
        <AlertDialogOverlay>
          <AlertDialogContent position="relative">
            <CloseButton position="absolute" right={2} top={2} onClick={onDeleteClose} />
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confirmar Desactivación
            </AlertDialogHeader>
            <AlertDialogBody>
              ¿Estás seguro de que deseas desactivar al usuario "{userToDelete?.name}"? 
              El usuario no podrá acceder al sistema hasta que sea reactivado.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancelar
              </Button>
              <Button colorScheme="red" onClick={confirmDelete} ml={3}>
                Desactivar
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </PageTable>
  );
}